/* @flow */
import * as React from 'react';
import { values } from 'lodash';
import { compose } from 'react-apollo';

import { withRouter } from 'app/context/Router';
import Row from 'components/Row';
import View from 'components/View';
import Views from 'components/Views';
import PeopleListConstants from 'components/PeopleList/PeopleListConstants';
import ListSidebar, {
  ORG_BATCH,
  GROUP_BATCH,
  USER_PROFILE,
} from 'components/ListSidebar/ListSidebar';
import PeopleListTableDataContainer from './PeopleListTableDataContainer';
import {
  PeopleListFilterUIDataContainerInOrg,
  PeopleListFilterUIDataContainerInGroup,
} from './PeopleListFilterUIDataContainer';
import SettingsStore from 'stores/SettingsStore';
import EventTracking from 'lib/EventTracking';
import GroupProfileContainer from 'groups/components/GroupProfile';
import {
  UsersProfileInOrgContainer,
  UsersProfileInGroupContainer,
} from 'users/components/UsersProfile';
import MediaObject from 'components/MediaObject';
import { PendingMembershipsButton } from 'groupMembershipRequests/components/PendingMemberships';
import { PendingRolesButton } from 'roleRequests/components/PendingRoles';

import type { ListActionsFilterState } from 'components/ListActions/ListActions';
import type { SubscribeInfoType } from 'entities/Group';
import type { UserProfile } from 'users/components/UsersProfile';

import type { PeopleListRow } from './PeopleListTable';
import type { PeopleListFilterState } from './PeopleListFilterUIDataContainer';
import type { SidebarData } from 'components/ListSidebar/ListSidebar';
import type { AdminOrganizationQuery_organizations } from 'groups/components/queries/__generated__/AdminOrganizationQuery';
import type { RouterState } from 'lib/ReactRouterTypes';
import type { SortDirection } from 'components/ComposableTable';

export type EntityType = 'organization' | 'group';
export type UserRole = 'teacher' | 'student' | 'parent' | 'admin' | 'unknown' | 'everyone';
type GroupType = 'owned' | 'subscribed' | 'organization' | 'district';

import './styles/PeopleList.scss';

export type GroupInfo = {
  id: number,
  uuid: string,
  name: string,
  groupType: GroupType,
  className: string,
  staffClassOrgId: ?number,
  hasChildren: boolean,
  pdfUrl: string,
  joinUrl: string,
  groupAvatar: ?{ svgUrl: ?string },
  subscribe: SubscribeInfoType,
  canAddOwner: boolean,
};

export type OrgInfo = $Shape<AdminOrganizationQuery_organizations>;

type Props = {
  entityType: EntityType,
  entityUuid: string,
  groupInfo: GroupInfo,
  associatedOrg: OrgInfo,
  isAdmin: boolean,
  onRemoveGroupFromOrg?: () => void,
  openModalRemoveGroupFromOrgEventKey: string,
  onViewAllAnnouncementsClicked?: () => void,
  router: RouterState,
};

type State = {
  query: string,
  filters: PeopleListFilterState,
  sidebarOpen: boolean,
  selectedUser: ?UserProfile,
  batchUsers: { [number]: PeopleListRow },
  reloadQuery: boolean,
  sidebarHistory: SidebarData[],
  currentSidebarData: ?SidebarData,
  sortColumn: ?string,
  sortDirection: ?SortDirection,
};

export class PeopleList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { entityType, router } = this.props;
    let { role } = router.location.query;

    if (!role) {
      role = entityType === 'group' ? 'everyone' : 'student';
    }

    this.state = {
      filters: { role },
      query: '',
      sidebarOpen: false,
      selectedUser: null,
      batchUsers: {},
      reloadQuery: false,
      currentSidebarData: null,
      sidebarHistory: [],
      sortColumn: null,
      sortDirection: null,
    };
  }

  trackEvent = (key: string) => {
    const { entityUuid } = this.props;

    EventTracking.trackKey(key, {
      organization_uuid: entityUuid,
    });
  };

  handleFiltersChange = (activeFilters: ListActionsFilterState) => {
    const { filters: currentFilters } = this.state;

    if (activeFilters.role !== currentFilters.role) {
      this.trackEvent('school_people_list.filters.role.changed');
    } else if (activeFilters.source !== currentFilters.source) {
      this.trackEvent('school_people_list.filters.source.changed');
    } else if (activeFilters.grade !== currentFilters.grade) {
      this.trackEvent('school_people_list.filters.grade.changed');
    }

    const validatedFilters = { ...activeFilters };

    if (
      validatedFilters.role !== 'student' ||
      validatedFilters.grade === PeopleListConstants.allGradesValue
    ) {
      validatedFilters.grade = null;
    }

    this.setState({ filters: validatedFilters });
  };

  handleQueryChange = (query: string) => {
    if (query === '') {
      this.trackEvent('school_people_list.search.cancel.click');
    }

    this.setState({ query });
  };

  handlePersonClicked = (row: Object) => {
    this.trackEvent('school_people_list.table.row.click');

    const user = {
      ...row.user,
      roles: row.roles,
    };

    this.setState({
      sidebarOpen: true,
      selectedUser: user,
      currentSidebarData: null,
      sidebarHistory: [],
    });
  };

  handleSidebarClose = () => {
    const { batchUsers } = this.state;
    this.setState({
      sidebarOpen: Object.keys(batchUsers).length > 0,
      selectedUser: null,
      currentSidebarData: null,
      sidebarHistory: [],
    });
  };

  handleSidebarEntityClicked = (newSidebarData: SidebarData) => {
    const { sidebarHistory } = this.state;
    const newHistory = sidebarHistory.slice();

    const currentSidebarData = this.getCurrentSidebarData();

    if (currentSidebarData) {
      newHistory.push(currentSidebarData);
    }

    this.setState({ sidebarHistory: newHistory, currentSidebarData: newSidebarData });
  };

  handleSidebarBackClicked = () => {
    const newHistory = this.state.sidebarHistory.slice();
    const sidebarData = newHistory.pop();
    this.setState({ sidebarHistory: newHistory, currentSidebarData: sidebarData });
  };

  deselectAll = () => {
    this.setState({
      sidebarOpen: false,
      batchUsers: {},
    });
  };

  addUserToSelection = (row: PeopleListRow) => {
    const { batchUsers } = this.state;

    if (Object.keys(batchUsers).length === 0) {
      this.trackEvent('school_people_list.table.batch.new');
    }

    this.setState({
      batchUsers: Object.assign({}, batchUsers, {
        [row.user.id]: row,
      }),
      sidebarOpen: true,
      selectedUser: null,
      currentSidebarData: null,
      sidebarHistory: [],
    });
  };

  removeUserFromSelection = (row: PeopleListRow) => {
    const { batchUsers } = this.state;
    const newUsers = Object.assign({}, batchUsers);
    delete newUsers[row.user.id];
    this.setState({
      batchUsers: newUsers,
      sidebarOpen: Object.keys(newUsers).length > 0,
      selectedUser: null,
      currentSidebarData: null,
      sidebarHistory: [],
    });
  };

  // TODO: from augusto - this is a really bad pattern, we should remove/refactor it in the future
  reloadList = () => {
    this.setState(
      {
        reloadQuery: true,
      },
      () => {
        this.setState({
          reloadQuery: false,
        });
      }
    );
  };

  getCodeForSelectedGradeFilter() {
    const {
      filters: { grade },
    } = this.state;

    if (grade) {
      return SettingsStore.getGradeLevelForName(grade).code || null;
    }

    return null;
  }

  getCurrentSidebarData() {
    const { entityType, entityUuid, associatedOrg, groupInfo } = this.props;
    const { selectedUser, batchUsers, currentSidebarData } = this.state;
    const batchUserValues = values(batchUsers);

    if (currentSidebarData) {
      return currentSidebarData;
    } else if (selectedUser) {
      return {
        type: USER_PROFILE,
        data: {
          member: selectedUser,
          entityUuid,
          entityType,
        },
      };
    } else if (batchUserValues.length > 0 && entityType === 'organization') {
      return {
        type: ORG_BATCH,
        data: {
          rows: batchUserValues,
          onDeselectAll: this.deselectAll,
          associatedOrg,
          reloadList: this.reloadList,
        },
      };
    } else if (batchUserValues.length > 0 && entityType === 'group') {
      return {
        type: GROUP_BATCH,
        data: {
          rows: batchUserValues,
          onDeselectAll: this.deselectAll,
          groupInfo,
          reloadList: this.reloadList,
        },
      };
    }

    return null;
  }

  handleSort = (sortColumn: string, sortDirection: ?SortDirection) => {
    this.setState({ sortColumn, sortDirection }, () => {
      this.reloadList();
    });
  };

  renderSidebar = () => {
    const {
      entityType,
      isAdmin,
      onRemoveGroupFromOrg,
      openModalRemoveGroupFromOrgEventKey,
      onViewAllAnnouncementsClicked,
    } = this.props;
    const { sidebarHistory, sidebarOpen, reloadQuery } = this.state;

    if (!sidebarOpen) {
      return null;
    }

    const currentSidebarData = this.getCurrentSidebarData();

    if (!currentSidebarData) {
      return null;
    }

    return (
      <View className="PeopleList-sidebar" full scroll>
        <ListSidebar
          sidebarData={currentSidebarData}
          onClose={this.handleSidebarClose}
          reloadQuery={reloadQuery}
          onUserChanged={this.reloadList}
          onEntityClicked={this.handleSidebarEntityClicked}
          onBackClicked={sidebarHistory.length > 0 ? this.handleSidebarBackClicked : undefined}
          onRemoveGroupFromOrg={onRemoveGroupFromOrg}
          openModalRemoveGroupFromOrgEventKey={openModalRemoveGroupFromOrgEventKey}
          onViewAllAnnouncementsClicked={onViewAllAnnouncementsClicked}
          userProfileView={
            entityType === 'group' ? UsersProfileInGroupContainer : UsersProfileInOrgContainer
          }
          groupProfileView={GroupProfileContainer}
          isAdmin={isAdmin}
        />
      </View>
    );
  };

  render() {
    const { entityType, entityUuid, groupInfo } = this.props;
    const {
      filters,
      query,
      selectedUser,
      batchUsers,
      reloadQuery,
      sortColumn,
      sortDirection,
    } = this.state;

    const { source, role } = filters;

    const rosterFilterType = (() => {
      if (source && source.length === 1) {
        return source[0];
      } else {
        return 'all';
      }
    })();

    const PeopleListFilterUIDataContainer =
      entityType === 'group'
        ? PeopleListFilterUIDataContainerInGroup
        : PeopleListFilterUIDataContainerInOrg;

    return (
      <Views className="PeopleList">
        <View className="PeopleList-body" gutter="small" scroll>
          <Row size="small">
            <MediaObject figure="right">
              <PeopleListFilterUIDataContainer
                entityType={entityType}
                entityUuid={entityUuid}
                filters={filters}
                onFiltersChange={this.handleFiltersChange}
                onQueryChange={this.handleQueryChange}
                reloadQuery={reloadQuery}
              />
              <React.Fragment>
                {entityType === 'group' && groupInfo.groupType === 'owned' && (
                  <PendingMembershipsButton groupName={groupInfo.name} groupUuid={entityUuid} />
                )}
                {entityType === 'organization' && (
                  <PendingRolesButton groupName={groupInfo.name} organizationUuid={entityUuid} />
                )}
              </React.Fragment>
            </MediaObject>
          </Row>
          <PeopleListTableDataContainer
            ref="table"
            entityType={entityType}
            entityUuid={entityUuid}
            groupInfo={groupInfo}
            query={query}
            grade={this.getCodeForSelectedGradeFilter()}
            role={role}
            rostered={rosterFilterType}
            onPersonClicked={this.handlePersonClicked}
            batchSelectedUserIds={Object.keys(batchUsers).map(k => parseInt(k, 10))}
            openUserId={selectedUser ? selectedUser.id : null}
            reloadQuery={reloadQuery}
            onUserChanged={this.reloadList}
            onUserAddedToSelection={this.addUserToSelection}
            onUserRemovedFromSelection={this.removeUserFromSelection}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={this.handleSort}
          />
        </View>
        {this.renderSidebar()}
      </Views>
    );
  }
}

export default compose(withRouter)(PeopleList);
